import React, { useEffect, useMemo, useState } from 'react';
import DOMPurify from 'dompurify';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, Mail, RefreshCw, Search, XCircle } from 'lucide-react';
import { EmailLog } from '../../types';
import { fetchEmailLogBody, fetchMyEmailLogs } from '../../services/cloudService';
import { Button } from '../Button';
import { IconButton, Panel, PanelHeader, SkeletonRows } from './dashboardUi';

interface Props {
    userId: string | null | undefined;
    open: boolean;
    onToggle: () => void;
    search: string;
    onSearch: (s: string) => void;
    projects: ReadonlyArray<{ id: string; project_number?: string | null }>;
}

type LogRow = EmailLog & { projectNumber?: string };

/**
 * Sendezentrale: die eigenen Versandprotokolle. Geladen wird ERST beim
 * Aufklappen — die Liste kommt ohne body_html; der Inhalt wird beim Oeffnen
 * eines Eintrags nachgeladen und vor dem Anzeigen mit DOMPurify bereinigt.
 */
export const SendCenterPanel: React.FC<Props> = ({ userId, open, onToggle, search, onSearch, projects }) => {
    // Geladen wird je (Nutzer, Neuladen-Zaehler); „laedt" ist der Vergleich
    // dieses Schluessels mit dem zuletzt geladenen — kein eigener Ladezustand,
    // den ein Effekt synchron setzen muesste.
    const [loaded, setLoaded] = useState<{ key: string; logs: EmailLog[] } | null>(null);
    const [selected, setSelected] = useState<LogRow | null>(null);
    const [reloadKey, setReloadKey] = useState(0);
    const loadKey = userId ? `${userId}:${reloadKey}` : '';
    const logs = loaded && loaded.key === loadKey ? loaded.logs : null;
    const loading = open && !!loadKey && logs === null;

    useEffect(() => {
        if (!open || !userId || !loadKey) return;
        let alive = true;
        fetchMyEmailLogs(userId)
            .then(l => { if (alive) setLoaded({ key: loadKey, logs: l }); })
            .catch(() => { if (alive) setLoaded({ key: loadKey, logs: [] }); });
        return () => { alive = false; };
    }, [open, userId, loadKey]);

    const rows = useMemo<LogRow[]>(() => {
        const term = search.trim().toLowerCase();
        return (logs || [])
            .map(l => ({ ...l, projectNumber: projects.find(p => p.id === l.project_id)?.project_number || '' }))
            .filter(l => !term || (l.subject || '').toLowerCase().includes(term) || (l.recipients || '').toLowerCase().includes(term));
    }, [logs, search, projects]);

    const openDetail = async (log: LogRow) => {
        setSelected(log);
        if (!log.body_html) {
            const body = await fetchEmailLogBody(log.id);
            if (body) setSelected(prev => (prev && prev.id === log.id) ? { ...prev, body_html: body } : prev);
        }
    };

    return (
        <Panel>
            <PanelHeader
                icon={<Mail />}
                title="Sendezentrale"
                sub={open && logs ? `${logs.length} Versandprotokolle` : undefined}
                actions={<>
                    {open && <IconButton label="Neu laden" onClick={() => setReloadKey(k => k + 1)} disabled={loading}><RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /></IconButton>}
                    <IconButton label={open ? 'Zuklappen' : 'Aufklappen'} onClick={onToggle} aria-expanded={open}>{open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</IconButton>
                </>}
            />
            {open && (<>
                <div className="px-3 py-2 border-b border-slate-100">
                    <div className="relative">
                        <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            type="search"
                            placeholder="Betreff oder Empfänger suchen…"
                            aria-label="Versandprotokolle durchsuchen"
                            className="w-full pl-8 pr-2 py-1.5 text-xs rounded-lg border border-slate-200 bg-white focus:border-blue-400"
                            value={search}
                            onChange={e => onSearch(e.target.value)}
                        />
                    </div>
                </div>
                {loading && !logs ? (
                    <SkeletonRows rows={3} />
                ) : rows.length === 0 ? (
                    <div className="p-4 text-center text-[11px] text-slate-500">Keine Versandprotokolle{search ? ' zu dieser Suche' : ''}</div>
                ) : (
                    <ul className="max-h-64 overflow-y-auto divide-y divide-slate-100">
                        {rows.slice(0, 30).map(l => (
                            <li key={l.id}>
                                <button type="button" onClick={() => openDetail(l)} className="w-full text-left px-3.5 py-2 hover:bg-slate-50 transition-colors">
                                    <div className="flex items-center justify-between gap-2">
                                        <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded border ${l.status === 'success' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`}>{l.status === 'success' ? 'Gesendet' : 'Fehler'}</span>
                                        <span className="text-[11px] text-slate-400 tabular">{new Date(l.sent_at).toLocaleDateString('de-DE')}</span>
                                    </div>
                                    <h4 className="text-xs font-semibold text-slate-700 truncate mt-1">{l.subject}</h4>
                                    <p className="text-[11px] text-slate-500 truncate">{l.projectNumber ? `${l.projectNumber} · ` : ''}{l.recipients}</p>
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </>)}

            {selected && <EmailDetailModal log={selected} onClose={() => setSelected(null)} />}
        </Panel>
    );
};

const EmailDetailModal: React.FC<{ log: LogRow; onClose: () => void }> = ({ log, onClose }) => {
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);
    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 animate-fade-in p-4" onClick={onClose}>
            <div role="dialog" aria-modal="true" aria-label="E-Mail-Details" className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
                <div className="p-3 border-b border-slate-200 flex justify-between items-center bg-slate-50 rounded-t-2xl">
                    <div className="flex items-center gap-2">
                        <div className={`p-1.5 rounded-lg ${log.status === 'success' ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-600'}`}>{log.status === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}</div>
                        <div><h3 className="font-semibold text-sm text-slate-800">Details</h3><p className="text-[11px] text-slate-500">{new Date(log.sent_at).toLocaleString('de-DE')}</p></div>
                    </div>
                    <button type="button" onClick={onClose} aria-label="Schließen" className="p-1 hover:bg-slate-200 rounded-full"><XCircle className="w-5 h-5 text-slate-400" /></button>
                </div>
                <div className="p-4 overflow-y-auto space-y-3">
                    <div><span className="text-[11px] uppercase font-semibold text-slate-400">Empfänger</span><div className="text-sm text-slate-600 font-mono mt-0.5 break-all">{log.recipients}</div></div>
                    <div><span className="text-[11px] uppercase font-semibold text-slate-400">Betreff</span><div className="font-semibold text-slate-800 mt-0.5">{log.subject}</div></div>
                    {log.error_message && <div className="bg-red-50 border border-red-100 p-3 rounded-xl text-red-700 text-sm">{log.error_message}</div>}
                    {log.body_html && <div className="border border-slate-200 rounded-xl overflow-hidden"><div className="p-3 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(log.body_html) }} /></div>}
                </div>
                <div className="p-3 border-t border-slate-200 bg-slate-50 rounded-b-2xl flex justify-end"><Button variant="secondary" onClick={onClose}>Schließen</Button></div>
            </div>
        </div>
    );
};
